/**
 * Werkstatt-Personalisierung (W1) — Frontend-Overlay, 1:1 zur Web-App
 * (public/assets/app.js: wkPersonalKey, wkHash, wkPersonalText, wkSchritt-Helfer,
 * wkAnpassen …).
 *
 * Rein additiv: die Kursdaten bleiben statisch; das personalisierte Ergebnis liegt
 * NUR im Browser (localStorage je Profil + Modul + Block — Postkarten-Regel, der
 * Server speichert nichts). promptKarte() in werkstatt.tsx schaut beim Rendern
 * nach, ob für eine prompt-id ein angepasster Text vorliegt.
 *
 *  - Klasse A (GPT-Overlay): die ganze Schrittfolge wird einmalig vom Backend
 *    umgeschrieben (POST /api/werkstatt/personalisieren), Hash je Original-Prompt.
 *  - Klasse B (Schritt-Eingaben): Dokumente/Zwischenergebnisse, rein clientseitig,
 *    OHNE GPT, OHNE Datenabfluss.
 *
 * Re-Render: ändert sich der Overlay-/Schritt-Stand, wird `wkRedraw` erhöht; der
 * Kurs-Screen (Course.tsx) lauscht darauf und rechnet die Projekt-Segmente neu
 * (App-Pendant zu wkNeuZeichnen).
 */
import { signal } from "@preact/signals";
import { endpoints } from "../api/endpoints";
import { ApiError } from "../api/client";
import { activeProfileId, getToken } from "../state/session";
import { loadQuota } from "../state/quota";
import { loadChapter, activeCourseId } from "../data/course-store";
import { createRecorder } from "../coach/recorder";
import { showToast } from "../ui/toast";
import type { WkAntwortItem } from "../api/types";

/* ---------- localStorage-Schlüssel (identisch zur Web-App) ---------- */
const personalKey = (mId: string, bId: string): string =>
  "wk-personal::" + activeProfileId() + "::" + mId + "::" + bId;
const schrittKey = (mId: string, bId: string): string =>
  "wk-schritt::" + activeProfileId() + "::" + mId + "::" + bId;

/* ---------- gespeicherte Strukturen ---------- */
interface PromptEintrag {
  text: string;
  hash: string;
}
export interface PersonalEintrag {
  projekt?: string;
  verstanden?: string;
  offen?: string[];
  prompts?: Record<string, PromptEintrag | string>;
  antworten?: WkAntwortItem[];
  freitext?: string;
}
type SchrittWerte = Record<string, string>;

export function ladePersonal(mId: string, bId: string): PersonalEintrag | null {
  try {
    return (JSON.parse(localStorage.getItem(personalKey(mId, bId)) || "null") as PersonalEintrag) || null;
  } catch {
    return null;
  }
}
function speicherePersonal(mId: string, bId: string, o: PersonalEintrag): void {
  try {
    localStorage.setItem(personalKey(mId, bId), JSON.stringify(o));
  } catch {
    /* Storage voll/gesperrt → still ignorieren (Web-Verhalten) */
  }
}
export function ladeSchritt(mId: string, bId: string): SchrittWerte {
  try {
    return (JSON.parse(localStorage.getItem(schrittKey(mId, bId)) || "{}") as SchrittWerte) || {};
  } catch {
    return {};
  }
}
function speichereSchritt(mId: string, bId: string, o: SchrittWerte): void {
  try {
    localStorage.setItem(schrittKey(mId, bId), JSON.stringify(o));
  } catch {
    /* still ignorieren */
  }
}

/* Kurzer, stabiler Hash des Original-Prompttexts (Befund 7.1): erkennt, ob ein
   Overlay nach einem Content-Update zu einem veralteten Original gehört. */
export function wkHash(s: unknown): string {
  let h = 0;
  const str = String(s ?? "");
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return h.toString(36);
}

/* Personalisierten Prompt-Text für eine prompt-id holen — oder null.
   Veraltete Overlays (Hash passt nicht zum aktuellen Original) werden ignoriert. */
export function personalText(
  mId: string,
  bId: string,
  promptId: string | undefined,
  originalText: string | null,
): string | null {
  const data = ladePersonal(mId, bId);
  if (!data || !data.prompts || !promptId) return null;
  const eintrag = data.prompts[promptId];
  if (!eintrag) return null;
  if (typeof eintrag === "string") return eintrag;
  if (eintrag.hash && originalText != null && eintrag.hash !== wkHash(originalText)) return null;
  return eintrag.text || null;
}

/* Klasse-B-Platzhalter (entstehen erst MITTEN im Projekt: Dokumente,
   Zwischenergebnisse) — die füllt der GPT NIE, sondern der Nutzer am Schritt. */
const SCHRITT_MUSTER = /(hier einf[üu]gen|aus schritt|dokument|transkript|ausschreibung|tabelle|briefing aus)/i;
export function istSchrittPlatzhalter(v: unknown): boolean {
  return SCHRITT_MUSTER.test(String(v ?? ""));
}

/* Setzt clientseitige Schritt-Eingaben (Klasse B) in einen Prompttext ein. */
export function setzeSchritt(text: string, schrittWerte: SchrittWerte | null): string {
  if (!schrittWerte) return text;
  return String(text).replace(/\[([^\]]+)\]/g, (m, name: string) => {
    const w = schrittWerte[name];
    return w && String(w).trim() ? String(w) : m;
  });
}

/* =========================================================================
   Re-Render-Brücke: Course.tsx erhöht beim Lauschen die Projekt-Segmente neu.
   ========================================================================= */
export const wkRedraw = signal(0);
function triggerRedraw(): void {
  wkRedraw.value = wkRedraw.value + 1;
}

/* =========================================================================
   Aktionen (App-Pendant zu wkAnpassen/wkReset/wkAendern/wkSchrittUebernehmen)
   ========================================================================= */

/** Original-Texte je prompt-id aus dem Projekt-Payload sammeln (für den Hash). */
function sammleOriginalTexte(raw: Record<string, unknown> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  const schritte = (raw?.schritte as Array<{ prompt?: { id?: string; text?: string } }> | undefined) || [];
  schritte.forEach((s) => {
    if (s.prompt && s.prompt.id) out[s.prompt.id] = String(s.prompt.text || "");
  });
  const probier = raw?.probier as { prompt_naiv?: { id?: string; text?: string } } | undefined;
  if (probier?.prompt_naiv?.id) out[probier.prompt_naiv.id] = String(probier.prompt_naiv.text || "");
  return out;
}

export interface AnpassenInput {
  courseId: string;
  moduleId: string;
  blockId: string;
  /** Roh-Payload des Projekt-Kapitels (für die Original-Hashes). */
  raw: Record<string, unknown> | null | undefined;
  antworten: WkAntwortItem[];
  freitext: string;
}
export type AnpassenResult =
  | { ok: true }
  | { ok: false; quota?: boolean; message: string };

/**
 * Ruft den Personalisierungs-Endpoint und speichert das Overlay lokal.
 * Gibt KEIN HTML zurück — der Aufrufer (Intake-Box-Handler) zeigt Status/Toast.
 */
export async function anpassen(input: AnpassenInput): Promise<AnpassenResult> {
  const { courseId, moduleId, blockId, raw, antworten, freitext } = input;
  if (!antworten.length && !freitext) {
    return { ok: false, message: "Beantworte mindestens eine Frage, dann passe ich die Prompts an." };
  }
  try {
    const data = await endpoints.personalisieren({
      course_id: courseId,
      module_id: moduleId,
      block_id: blockId,
      antworten,
      freitext,
    });
    if (data.status !== "ok" || !data.prompts || !Object.keys(data.prompts).length) {
      return { ok: false, message: "Das hat nicht geklappt — versuch es nochmal oder arbeite mit dem Beispiel." };
    }
    // Overlay mit Hash je Original speichern (Befund 7.1) + Antworten (Befund 7.2).
    const origText = sammleOriginalTexte(raw);
    const prompts: Record<string, PromptEintrag> = {};
    Object.keys(data.prompts).forEach((pid) => {
      prompts[pid] = { text: data.prompts[pid], hash: wkHash(origText[pid] || "") };
    });
    const eintrag: PersonalEintrag = {
      projekt: blockId,
      verstanden: data.verstanden || "",
      offen: data.offen || [],
      prompts,
      antworten,
      freitext,
    };
    speicherePersonal(moduleId, blockId, eintrag);
    triggerRedraw();
    // Kontingent zählt mit → Token-Balken auffrischen (nice to have).
    void loadQuota(true);
    return { ok: true };
  } catch (err) {
    if (err instanceof ApiError && err.status === 429) {
      return {
        ok: false,
        quota: true,
        message:
          "Dein Kontingent ist gerade aufgebraucht. Deine letzte gelungene Anpassung bleibt erhalten; du kannst auch mit dem Beispiel weiterarbeiten.",
      };
    }
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      // Client hat bereits ausgeloggt; neutraler Hinweis.
      return { ok: false, message: "Bitte zuerst anmelden." };
    }
    const detail = err instanceof ApiError && err.detail ? err.detail : "";
    return {
      ok: false,
      message: detail || "Das hat nicht geklappt — versuch es nochmal oder arbeite mit dem Beispiel.",
    };
  }
}

/** Personalisierung zurücksetzen (zurück zum Beispiel). */
export function reset(moduleId: string, blockId: string): void {
  try {
    localStorage.removeItem(personalKey(moduleId, blockId));
  } catch {
    /* ignore */
  }
  triggerRedraw();
}

/** „Angaben ändern": gespeichertes verstanden verwerfen → Intake-Formular zeigen
   (Antworten bleiben in localStorage und befüllen die Felder beim Neuzeichnen vor). */
export function aendern(moduleId: string, blockId: string): void {
  const data = ladePersonal(moduleId, blockId) || {};
  delete data.verstanden;
  speicherePersonal(moduleId, blockId, data);
  triggerRedraw();
}

/** Schritt-Eingaben (Klasse B) speichern + in den Prompt einsetzen. */
export function schrittUebernehmen(moduleId: string, blockId: string, werte: SchrittWerte): void {
  const aktuell = ladeSchritt(moduleId, blockId);
  Object.keys(werte).forEach((k) => {
    aktuell[k] = werte[k];
  });
  speichereSchritt(moduleId, blockId, aktuell);
  triggerRedraw();
}

/* =========================================================================
   Klick-Delegation — App-Pendant zur globalen Delegation in app.js
   (aktiviereAskKi, Z. 1669-1704). Wird aus handleLessonClick aufgerufen,
   bevor die generischen Aktionen (Kopieren etc.) greifen.
   ========================================================================= */

/** Intake-Formular auslesen → Antworten + Freitext (für anpassen). */
function leseIntake(box: HTMLElement): { antworten: WkAntwortItem[]; freitext: string } {
  const antworten = Array.from(box.querySelectorAll<HTMLTextAreaElement>("[data-wk-frage]"))
    .map((el) => ({
      frage_id: el.dataset.wkFrage || "",
      frage: el.dataset.wkFrageText || "",
      text: (el.value || "").trim(),
    }))
    .filter((a) => a.text);
  const freitextEl = box.querySelector<HTMLTextAreaElement>("[data-wk-freitext]");
  const freitext = freitextEl ? (freitextEl.value || "").trim() : "";
  return { antworten, freitext };
}

/** „Prompts auf mein Vorhaben anpassen" — liest die Box, ruft das Backend. */
async function handleAnpassen(box: HTMLElement): Promise<void> {
  const moduleId = box.dataset.module || "";
  const blockId = box.dataset.block || "";
  const statusEl = box.querySelector<HTMLElement>("[data-wk-status]");
  const btn = box.querySelector<HTMLButtonElement>("[data-wk-anpassen]");
  if (!getToken()) {
    if (statusEl) statusEl.textContent = "Bitte zuerst anmelden.";
    return;
  }
  const { antworten, freitext } = leseIntake(box);
  if (!antworten.length && !freitext) {
    if (statusEl) statusEl.textContent = "Beantworte mindestens eine Frage, dann passe ich die Prompts an.";
    return;
  }
  if (btn) btn.disabled = true;
  if (statusEl) statusEl.textContent = "Ich lese dein Vorhaben und passe die Schritte an …";
  // Roh-Payload aus dem Kapitel-Cache (für die Original-Hashes) — beim offenen
  // Projekt ist das Kapitel bereits geladen, der Aufruf löst sich aus dem Cache.
  let raw: Record<string, unknown> | null = null;
  try {
    const ch = await loadChapter(moduleId, blockId, activeCourseId.value);
    raw = (ch.raw ?? null) as Record<string, unknown> | null;
  } catch {
    /* ohne raw: Hash wird leer — Overlay greift trotzdem, nur Hash-Schutz entfällt */
  }
  const res = await anpassen({
    courseId: activeCourseId.value,
    moduleId,
    blockId,
    raw,
    antworten,
    freitext,
  });
  if (res.ok) {
    // Box wird durch Redraw ersetzt; Toast als Bestätigung (Web zeigt zeigeToast).
    showToast("Prompts auf dein Vorhaben angepasst ✓");
    return;
  }
  if (statusEl) statusEl.textContent = res.message;
  if (btn) btn.disabled = false; // Knopf wieder aktivieren (auch bei 429)
}

/* Mikrofon im Intake (Spracheingabe ins Fragefeld). Nutzt dieselbe Transkription
   wie der Coach (createRecorder → /api/transcribe). Ein Recorder pro Klick. */
let mikroAktiv: { dispose: () => void } | null = null;
function handleMikro(btn: HTMLElement): void {
  const ziel = btn.previousElementSibling as HTMLTextAreaElement | null;
  // Wenn schon eine Aufnahme läuft: stoppen (Toggle).
  if (mikroAktiv) {
    mikroAktiv.dispose();
    mikroAktiv = null;
    btn.classList.remove("is-recording");
    return;
  }
  const rec = createRecorder({
    onText: (text) => {
      const feld =
        ziel ||
        (btn.closest(".wk-frage-eingabe")?.querySelector<HTMLTextAreaElement>("[data-wk-frage]") ?? null);
      if (feld) {
        feld.value = feld.value ? feld.value + " " + text : text;
        feld.dispatchEvent(new Event("input", { bubbles: true }));
      }
    },
    onNotice: (msg) => showToast(msg),
  });
  // Aufnahme-Status spiegelt die Knopf-Optik (puls), wie im Web (is-recording).
  const unsub = rec.state.subscribe((s) => {
    if (s === "recording") btn.classList.add("is-recording");
    else btn.classList.remove("is-recording");
    if (s === "idle") {
      mikroAktiv = null;
      unsub();
    }
  });
  mikroAktiv = { dispose: () => { rec.dispose(); unsub(); } };
  void rec.toggle();
}

/**
 * Behandelt die Werkstatt-Personalisierungs-Klicks. Gibt true zurück, wenn der
 * Klick verarbeitet wurde (Aufrufer macht dann preventDefault + return).
 */
export function handleWerkstattClick(ev: MouseEvent): boolean {
  const t = ev.target as Element | null;
  if (!t || !t.closest) return false;

  const anp = t.closest<HTMLElement>("[data-wk-anpassen]");
  if (anp) {
    ev.preventDefault();
    const box = anp.closest<HTMLElement>("[data-wk-intake]");
    if (box) void handleAnpassen(box);
    return true;
  }
  const rst = t.closest<HTMLElement>("[data-wk-reset]");
  if (rst) {
    ev.preventDefault();
    const box = rst.closest<HTMLElement>("[data-wk-intake]");
    if (box) {
      reset(box.dataset.module || "", box.dataset.block || "");
      showToast("Zurück zum Beispiel ✓");
    }
    return true;
  }
  const aen = t.closest<HTMLElement>("[data-wk-aendern]");
  if (aen) {
    ev.preventDefault();
    const box = aen.closest<HTMLElement>("[data-wk-intake]");
    if (box) aendern(box.dataset.module || "", box.dataset.block || "");
    return true;
  }
  const schrAp = t.closest<HTMLElement>("[data-wk-schritt-apply]");
  if (schrAp) {
    ev.preventDefault();
    const box = schrAp.closest<HTMLElement>("[data-wk-schritt-box]");
    if (box && box.dataset.module && box.dataset.block) {
      const werte: SchrittWerte = {};
      box.querySelectorAll<HTMLTextAreaElement>("[data-wk-schritt]").forEach((el) => {
        werte[el.dataset.wkSchritt || ""] = el.value || "";
      });
      schrittUebernehmen(box.dataset.module, box.dataset.block, werte);
      showToast("In den Prompt übernommen ✓");
    }
    return true;
  }
  const orig = t.closest<HTMLElement>("[data-wk-orig]");
  if (orig) {
    ev.preventDefault();
    const karte = orig.closest<HTMLElement>(".wk-prompt");
    if (!karte) return true;
    const pre = karte.querySelector<HTMLElement>(".wk-prompt-text");
    const zeigtOrig = orig.getAttribute("aria-pressed") === "true";
    if (zeigtOrig) {
      // zurück zur angepassten Ansicht: robust = neu zeichnen (Web wkNeuZeichnen).
      triggerRedraw();
    } else if (pre) {
      pre.innerHTML = promptVarsHtml(orig.getAttribute("data-wk-orig-text") || "");
      orig.setAttribute("aria-pressed", "true");
      orig.textContent = "Angepasste Fassung anzeigen";
    }
    return true;
  }
  const mikro = t.closest<HTMLElement>("[data-wk-mikro]");
  if (mikro) {
    ev.preventDefault();
    handleMikro(mikro);
    return true;
  }
  return false;
}

/* Prompt-Text mit dezent hervorgehobenen [Variablen] — gespiegelt aus werkstatt.tsx
   promptHtml (hier dupliziert, um keinen Import-Zyklus content↔content zu öffnen). */
function escForVars(s: unknown): string {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function promptVarsHtml(text: string): string {
  return escForVars(text).replace(/\[([^\]]+)\]/g, '<span class="wk-var">[$1]</span>');
}
